/**
 * sl-8n5m.9.1: MarkdownRenderer ViewComponent
 *
 * Renders a single row's body column as sanitized Markdown (GFM).
 * Language fences emit `<pre><code class="language-xxx">` for CSS-based
 * highlighters; no highlighter runtime is bundled.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';
import { PlaceholderWarning } from '../../app-base/placeholder-warning';
import * as styles from './markdown-renderer.css';
import { renderSanitizedMarkdown } from './markdown-sanitizer';

const configSchema = z.object({
  bodyField: z.string().default('body'),
});

export const MarkdownRendererComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.MARKDOWN_RENDERER,
  type: 'content',
  name: 'Markdown Content',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    'M↓'
  ),
  capabilities: [
    {
      role: 'body',
      description: 'Markdown body content',
      required: true,
      requirements: [
        { field: 'body', acceptedTypes: ['text'], required: true },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    bodyField: 'body',
  },
  render({ config, data }) {
    const typedConfig = config as z.infer<typeof configSchema>;
    const row = data[0];
    const md = row
      ? (row[typedConfig.bodyField] as string | undefined)
      : undefined;

    if (!md || md.trim() === '') {
      return createElement(PlaceholderWarning, {
        reasonKey: 'bodyMissing',
        role: 'body',
      });
    }

    const sanitized = renderSanitizedMarkdown(md);
    return createElement('div', {
      className: styles.markdownContent,
      'data-sl-field': 'body',
      dangerouslySetInnerHTML: { __html: sanitized },
    });
  },
  palettePreview() {
    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-app-padding-sm)',
          fontSize: 'var(--sl-app-font-size-xs)',
          color: 'var(--streamlain-v2-text-secondary)',
        },
      },
      'M↓ Markdown'
    );
  },
};
